import { useCallback, useEffect, useState } from "react";
import { ExternalLink, Globe, LoaderCircle, Lock } from "lucide-react";
import { api } from "../lib/api";
import { errorMessage } from "../lib/firebase";

type Status = Awaited<ReturnType<typeof api.siteStatus>>;

/** Opens or closes the public site at the edge. While closed every portal URL shows the "en construcción" page. */
export function Site() {
  const [status, setStatus] = useState<Status | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    api
      .siteStatus({})
      .then(setStatus)
      .catch((e) => setError(errorMessage(e)));
  }, []);

  useEffect(load, [load]);
  // CloudFront takes a minute or two to roll the change out; keep checking until it is live everywhere.
  useEffect(() => {
    if (!status?.pending) return;
    const t = setInterval(load, 10_000);
    return () => clearInterval(t);
  }, [status?.pending, load]);

  async function apply() {
    if (!status) return;
    setBusy(true);
    setError(null);
    try {
      await api.setSiteStatus({ maintenance: !status.maintenance });
      setConfirming(false);
      load();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  const closed = status?.maintenance;
  return (
    <div className="page">
      <header className="toolbar">
        <div className="toolbar-title">
          <h1>Sitio público</h1>
          <p className="muted small">Abrí o cerrá odontopilar.com.ar. Cerrado, cualquier dirección del portal muestra la página «En construcción».</p>
        </div>
      </header>
      {error && <p className="notice notice-danger">{error}</p>}

      {!status ? (
        <div className="skeleton-rows" aria-busy="true" />
      ) : (
        <section className="site-state" data-closed={closed || undefined} aria-live="polite">
          <div className="site-state-head">
            <span className="site-state-icon" aria-hidden>
              {closed ? <Lock size={22} strokeWidth={1.5} /> : <Globe size={22} strokeWidth={1.5} />}
            </span>
            <div>
              <p className="label">Estado actual</p>
              <h2>{closed ? "Cerrado · En construcción" : "Abierto al público"}</h2>
              {status.pending ? (
                <p className="small site-state-pending">
                  <LoaderCircle className="spin" size={14} aria-hidden /> Aplicando el cambio en todo el mundo (1–3 minutos)…
                </p>
              ) : (
                status.updatedAt && (
                  <p className="muted small">
                    Último cambio {new Date(status.updatedAt).toLocaleString("es-AR", { dateStyle: "short", timeStyle: "short" })}
                    {status.updatedBy ? ` · ${status.updatedBy}` : ""}
                  </p>
                )
              )}
            </div>
          </div>

          <p className="site-state-text">
            {closed
              ? "Nadie puede ver el portal, ni siquiera con un enlace directo a una página interna. Las cuentas del equipo lo siguen viendo normal desde el navegador donde ingresaron al panel."
              : "Cualquier persona puede navegar el sitio y reservar turnos."}
          </p>

          <div className="row gap-8 wrap">
            {confirming ? (
              <>
                <button className={closed ? "btn btn-primary" : "btn btn-danger"} onClick={apply} disabled={busy}>
                  {busy && <LoaderCircle className="spin" size={16} aria-hidden />} {closed ? "Sí, abrir el sitio" : "Sí, cerrar el sitio"}
                </button>
                <button className="btn" onClick={() => setConfirming(false)} disabled={busy}>
                  Cancelar
                </button>
              </>
            ) : (
              <button className={closed ? "btn btn-primary" : "btn btn-danger"} onClick={() => setConfirming(true)} disabled={status.pending}>
                {closed ? "Abrir el sitio" : "Cerrar el sitio"}
              </button>
            )}
            <a className="btn" href="/mantenimiento/" target="_blank" rel="noreferrer">
              Ver la página «En construcción» <ExternalLink size={14} aria-hidden />
            </a>
          </div>
        </section>
      )}
    </div>
  );
}
